import Image from "next/image";
import homeData from "@data/pages/home.json";

interface ClientLogoItem {
  name: string;
  logo: string;
  logoAlt?: string;
}

const clients: ClientLogoItem[] = homeData.clientsTrust.clients;

/** Per-logo max-height overrides — mirrors ClientsTrustSection to keep visual balance.
 * Takeda's tightly-cropped wordmark renders much taller than icon-style logos at the same max-h. */
const logoSizeOverrides: Record<string, string> = {
  GLS: "max-h-[5.5rem]",
  "Siemens Energy": "max-h-[5.5rem]",
  Takeda: "max-h-[1.8rem]",
  AXA: "max-h-[2.75rem]",
  Jettainer: "max-h-20",
  "Seven.One Entertainment Group": "max-h-[2.45rem]",
  MDR: "max-h-[2.45rem]",
  "PFALZWERKE GRUPPE": "max-h-[2.45rem]",
};
const logoSizeDefault = "max-h-14";

export default function ClientLogosMarquee() {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white py-8">
      <div
        className="marquee-track flex w-max items-center gap-10 px-6"
        aria-label="Referenzkunden"
      >
        {clients.map((client) => {
          const sizeClass = logoSizeOverrides[client.name] ?? logoSizeDefault;
          return (
            <div
              key={client.name}
              className="flex h-20 w-52 flex-shrink-0 items-center justify-center"
            >
              <Image
                src={client.logo}
                alt={client.logoAlt ?? `${client.name} Kundenlogo`}
                width={220}
                height={80}
                className={`${sizeClass} w-auto object-contain grayscale opacity-70 transition hover:grayscale-0 hover:opacity-100`}
              />
            </div>
          );
        })}
        {/* Duplicate set for seamless loop — hidden from assistive tech and crawlers */}
        {clients.map((client) => {
          const sizeClass = logoSizeOverrides[client.name] ?? logoSizeDefault;
          return (
            <div
              key={`${client.name}-dup`}
              aria-hidden="true"
              className="flex h-20 w-52 flex-shrink-0 items-center justify-center"
            >
              <Image
                src={client.logo}
                alt=""
                width={220}
                height={80}
                className={`${sizeClass} w-auto object-contain grayscale opacity-70 transition hover:grayscale-0 hover:opacity-100`}
              />
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .marquee-track {
          animation: logos-marquee 36s linear infinite;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
        @keyframes logos-marquee {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
